import { CognitoUser } from 'amazon-cognito-identity-js'
import * as React from 'react'
import { Controller, useForm } from 'react-hook-form'

import { Intent } from '../../../config/intent'
import { getVerificationText } from '../../../lib/auth-helpers'
import { authService } from '../../../lib/auth-service'
import { Validation } from '../../../lib/validation'
import { Button } from '../../button'
import { Checkbox } from '../../checkbox'
import { FormGroup } from '../../form-group'
import { VerificationCodeInput } from '../../verification-code-input'
import { AuthHeading } from '../auth-heading/auth-heading'

export const TestId = {
    FORM: 'verification-code-form',
}

export const I18n = {
    HEADING: 'Enter 2FA 6-digit code',
    CODE_LABEL: 'Code*',
    CODE_INVALID: 'Invalid code',
    CODE_NOT_DELIVERED: "Didn't get the code?",
    CODE_RESEND: 'Resend now',
    SUBMIT: 'Submit',
}

export interface VerificationCodeFormValues {
    code: string
    remember: boolean
}

export interface VerificationCodeFormProps {
    user: CognitoUser
    onComplete: (user: CognitoUser, data: VerificationCodeFormValues) => void
}

export function VerificationCodeForm({
    user,
    onComplete,
}: VerificationCodeFormProps) {
    const [formError, setFormError] = React.useState('')
    const [loading, setLoading] = React.useState(false)

    const {
        handleSubmit,
        formState: { errors },
        setError,
        control,
    } = useForm<VerificationCodeFormValues>()

    function handleError(err: any) {
        if (err.code === 'CodeMismatchException') {
            setError('code', { message: I18n.CODE_INVALID })
        } else {
            setFormError(err.message)
        }
    }

    async function onSubmit(data: VerificationCodeFormValues) {
        setLoading(true)

        try {
            const authenticatedUser = await authService.confirmSignIn({
                user,
                code: data.code,
            })
            onComplete(authenticatedUser, data)
        } catch (err) {
            setLoading(false)
            handleError(err)
        }
    }

    // TODO: check this is working
    async function resendCode(e: any) {
        e.preventDefault()
        try {
            await authService.resendCode('phone_number')
        } catch (err) {
            handleError(err)
        }
    }

    return (
        <React.Fragment>
            <AuthHeading
                heading={I18n.HEADING}
                subHeading={getVerificationText(
                    // @ts-ignore
                    user?.challengeParam?.CODE_DELIVERY_DELIVERY_MEDIUM,
                    // @ts-ignore
                    user?.challengeParam?.CODE_DELIVERY_DESTINATION,
                )}
            />
            <form onSubmit={handleSubmit(onSubmit)} data-testid={TestId.FORM}>
                <FormGroup
                    label={I18n.CODE_LABEL}
                    htmlFor='code'
                    intent={
                        errors.code?.message ? Intent.DANGER : Intent.DEFAULT
                    }
                    message={errors.code?.message}>
                    <Controller
                        name='code'
                        control={control}
                        rules={Validation.verificationCode}
                        render={({ field }) => {
                            return (
                                <VerificationCodeInput
                                    name='code'
                                    onChange={value => field.onChange(value)}
                                    intent={
                                        errors.code?.message
                                            ? Intent.DANGER
                                            : Intent.DEFAULT
                                    }
                                />
                            )
                        }}
                    />
                </FormGroup>
                <FormGroup className='text-gray'>
                    <span>{I18n.CODE_NOT_DELIVERED}</span>{' '}
                    <button
                        onClick={resendCode}
                        className='text-black underline'
                        type='button'>
                        {I18n.CODE_RESEND}
                    </button>
                </FormGroup>
                <FormGroup className='mb-10'>
                    <Checkbox
                        value={true}
                        onChange={() => {}}
                        label='Remember this device for 30 days'
                    />
                </FormGroup>

                <Button
                    loading={loading}
                    disabled={loading}
                    fill={true}
                    type='submit'
                    size='large'
                    className='mb-4'>
                    {I18n.SUBMIT}
                </Button>
                {formError && (
                    <p className='text-center text-danger'>{formError}</p>
                )}
                <p className='text-xs mt-10'>
                    Your application, account, and device information and use
                    patterns will be shared with Moon Laboratories Inc,
                    financial partners, and service providers for application
                    processing, identity and entity verification, fraud
                    prevention, security, to tailor services to you, comply with
                    our legal obligations, and communicate with you. Privacy
                    Policy
                </p>
            </form>
        </React.Fragment>
    )
}
